/**
 * VI-725 / VI-773 — contain the v2 theme's runaway icon duplication inside the platforms web
 * components.
 *
 * ## The bug (measured in QA)
 * The v2 theme's icon converter (`v2/iife/900.chunk.min.js`) swaps `<i class="fa-…">` for an
 * inline `<svg>` using `replaceChild()`. Inside the Stencil components shipped by
 * `@jobtarget/shared-components` / `interviewer-ui`, Stencil has already re-parented that `<i>`,
 * so `replaceChild` throws:
 *
 *     NotFoundError: Failed to execute 'replaceChild' on 'Node':
 *     The node to be replaced is not a child of this node.
 *
 * The original `<i>` survives, but the freshly built `<svg>` is still appended. That DOM change
 * re-triggers the converter's MutationObserver, which leaks another `<svg>`, and so on — we
 * measured 1,617 chevrons growing to 2,278 in ~4s, and >1,400 duplicated Edit/Detach icons in the
 * View Job panel, with the NotFoundError repeating for each one.
 *
 * ## Upstream status
 * Tracked as PA-1976. Only the job-bar chevron was ever addressed (MR !353); the equivalent fix
 * for `<ivr-view-qnr>` (Edit / Detach) was closed unmerged, and the build published to our QA
 * component-library CDN still duplicates. This guard is the interim containment.
 *
 * NOTE: the guard cannot stop the `NotFoundError`s themselves — the converter throws before we
 * ever see the DOM. It contains the *consequences*: unbounded DOM growth and the empty-shell
 * job bar. Silencing the errors requires the upstream fix.
 *
 * ## What this does
 * Scoped strictly to the platform custom elements (never our own markup), for each affected icon:
 *   1. keep the FIRST leaked `<svg>` in a given parent — it is the correctly rendered FontAwesome
 *      icon, so the UI still shows the right glyph at the right weight; and
 *   2. delete every other copy in that same parent, then hide the orphaned `<i>` the converter
 *      failed to replace (hiding rather than restyling avoids rendering the icon twice).
 * If nothing has leaked we leave the element completely alone, so icons the converter handles
 * correctly keep their normal behaviour.
 *
 * VI-773 widened this in two ways, after QA hit the same loop in the job *filter* (Select Job /
 * Change or Clear Job) — ~189-200 throws per few open/close cycles, with the bar going blank:
 *   - the de-dupe is now generic (any `svg[data-icon]`) instead of a three-icon allowlist, so
 *     the picker's own icons — chevrons, close, calendar — are covered too; and
 *   - `change-job` / `base-offcanvas` were added to the roots below; the picker subtree was
 *     previously never swept at all.
 * De-duplication is deliberately scoped to each icon's IMMEDIATE PARENT rather than the whole
 * root: the converter appends its replacement as a sibling of the `<i>` it failed to swap, so
 * copies always pile up inside one parent. Per-parent scoping is what lets genuinely repeated
 * icons survive — e.g. one chevron per job row in the picker list — which a root-wide
 * "keep one of each icon" pass would have wrongly stripped.
 *
 * DELETE THIS once the upstream fix is published to our component-library CDN.
 */

import { iconGuardsDisabled } from './iconGuardDiagnostics';


/**
 * Platform-owned custom elements only. Scoping by these tags is what keeps the guard away from
 * our own icons — e.g. our questionnaire preview modal legitimately renders `fa-pen-to-square`.
 * We never render any of these tags ourselves (our own drawer is components/common/OffCanvas).
 */
const PLATFORM_ROOTS =
  'job-bar, job-control-panel, change-job, base-offcanvas, ivr-view-qnr, ivr-questionnaire-link-status';

/**
 * Every class the converter uses to recognise an icon: the style shorthands (`fa`, `fas`, `fal`…)
 * and every `fa-*` name/modifier. Stripping all of them is what makes an orphaned `<i>` invisible
 * to the converter so it stops re-converting it (see step 2 of the sweep).
 */
const FA_CLASS_PATTERN = /^(fa|fas|far|fal|fab|fad|fat|fa-.*)$/;

/**
 * @deprecated VI-784 — no longer applied. FontAwesome copies the `<i>`'s classList onto the `<svg>`
 * it builds, so this marker rode along onto the real icon and the CSS rule hid the visible glyph.
 * Orphans are now hidden with an inline style instead. Kept only so the CSS rule and any external
 * reference keep resolving; safe to delete with the guard.
 */
export const NEUTRALIZED_CLASS = 'jt-platform-icon-orphan';

const sweep = (): void => {
  document.querySelectorAll<HTMLElement>(PLATFORM_ROOTS).forEach((root) => {
    // 1. Drop duplicate <svg>s, scoped to each icon's immediate parent (see the note above).
    //    keptByParent also records which icons legitimately rendered in that parent, which
    //    step 2 uses to decide whether an <i> has been superseded.
    const keptByParent = new Map<Element, Set<string>>();

    root.querySelectorAll<SVGElement>('svg[data-icon]').forEach((svg) => {
      const parent = svg.parentElement;
      // Never touch content we projected into the platform component's slots.
      if (!parent || svg.closest('[data-jt-slot]')) return;

      const icon = svg.getAttribute('data-icon');
      if (!icon) return;

      let kept = keptByParent.get(parent);
      if (!kept) {
        kept = new Set<string>();
        keptByParent.set(parent, kept);
      }

      if (kept.has(icon)) {
        svg.remove(); // a duplicate of one we already kept in this parent
        return;
      }
      kept.add(icon);
    });

    // 2. Neutralise the `<i>` the converter could not replace — but only where a replacement for
    //    the SAME icon exists in the same parent. Otherwise that `<i>` is the only thing rendering
    //    the icon and hiding it would blank the glyph (v2 ships no icon webfont).
    //
    //    VI-784: we must STRIP the fa-* classes, not merely hide the node. While the orphan still
    //    carries `fa-angle-right`, the converter keeps matching it on every mutation, appends yet
    //    another <svg>, and throws again — that re-match IS the infinite loop (QA measured 9,000+
    //    errors). Removing the classes makes the orphan invisible to the converter, which is what
    //    actually terminates it. Hiding is done with an inline style rather than a class because
    //    FontAwesome COPIES the <i>'s classList onto the <svg> it builds, so a marker class would
    //    ride along onto the real icon and our CSS would then hide the visible glyph.
    root.querySelectorAll<HTMLElement>('i[class*="fa-"]').forEach((orphan) => {
      const parent = orphan.parentElement;
      if (!parent || orphan.closest('[data-jt-slot]')) return;

      const kept = keptByParent.get(parent);
      if (!kept) return;

      // `fa-angle-right` -> `angle-right`, matched against the <svg>'s data-icon. Style/modifier
      // classes (fa-solid, fa-fw, fa-2x…) simply never match a data-icon, so they're inert here.
      const superseded = Array.from(orphan.classList).some(
        (cls) => cls.startsWith('fa-') && kept.has(cls.slice(3))
      );
      if (!superseded) return;

      Array.from(orphan.classList)
        .filter((cls) => FA_CLASS_PATTERN.test(cls))
        .forEach((cls) => orphan.classList.remove(cls));
      orphan.style.display = 'none';
      orphan.setAttribute('data-jt-icon-neutralised', 'true');
    });
  });
};

/**
 * Installs the guard. Returns a cleanup function that disconnects it.
 * Safe to call once per app; the observer detaches itself while mutating so its own edits
 * cannot feed the duplication loop it is suppressing.
 */
export function installPlatformIconGuard(): () => void {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => undefined;
  }
  // VI-784 attribution switch — see utils/iconGuardDiagnostics.ts
  if (iconGuardsDisabled()) return () => undefined;

  let queued = false;
  let observer: MutationObserver | null = null;

  const run = () => {
    queued = false;
    if (!observer) return;
    observer.disconnect();
    try {
      sweep();
    } finally {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    // Coalesce the burst of mutations the loop produces into one sweep per frame.
    requestAnimationFrame(run);
  });

  observer.observe(document.body, { childList: true, subtree: true });
  sweep();

  return () => {
    observer?.disconnect();
    observer = null;
  };
}
